import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';

// Layouts
import MainLayout from './layouts/MainLayout';
import DashboardLayout from './layouts/DashboardLayout';

// Pages publiques
import HomePage from './pages/HomePage';
import ApartmentListPage from './pages/ApartmentListPage';
import ApartmentDetailPage from './pages/ApartmentDetailPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';

// Pages Locataire
import LocataireDashboard from './pages/locataire/Dashboard';
import LocataireBookings from './pages/locataire/Bookings';
import BookingDetailPage from './pages/locataire/BookingDetail';
import ReservationSearchPage from './pages/locataire/ReservationSearch';
import BookingPage from './pages/BookingPage';

// Pages Gérant
import GerantDashboard from './pages/gerant/Dashboard';
import GerantApartments from './pages/gerant/Apartments';
import GerantApartmentForm from './pages/gerant/ApartmentForm';
import GerantCalendar from './pages/gerant/Calendar';
import GerantBookings from './pages/gerant/Bookings';
import GerantInvoices from './pages/gerant/Invoices';
import CreateInvitation from './pages/gerant/CreateInvitation';

// Pages Admin (Super User)
import AdminDashboard from './pages/admin/Dashboard';
import AdminGerants from './pages/admin/Gerants';
import AdminLocataires from './pages/admin/Locataires';
import AdminApartments from './pages/admin/Apartments';
import AdminBookings from './pages/admin/Bookings';
import AdminMessages from './pages/admin/Messages';
import AdminNotifications from './pages/admin/Notifications';

// Pages communes
import MessagesPage from './pages/MessagesPage';
import NotificationsPage from './pages/NotificationsPage';
import ProfilePage from './pages/ProfilePage';
import NotFoundPage from './pages/NotFoundPage';
import InvitationPage from './pages/InvitationPage';

// Route protégée
interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  const { checkAuth } = useAuthStore();

  React.useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <Routes>
      {/* Routes publiques avec MainLayout */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/apartments" element={<ApartmentListPage />} />
        <Route path="/apartments/:id" element={<ApartmentDetailPage />} />
        <Route path="/reservation" element={<ReservationSearchPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
      </Route>

      {/* Route de réservation (accessible à tous) */}
      <Route element={<MainLayout />}>
        <Route path="/booking/:apartmentId" element={<BookingPage />} />
      </Route>

      {/* Route d'invitation (publique - pour paiement) */}
      <Route path="/invitation/:token" element={<InvitationPage />} />

      {/* Routes Locataire */}
      <Route
        path="/locataire"
        element={
          <ProtectedRoute allowedRoles={['ROLE_LOCATAIRE']}>
            <DashboardLayout userType="locataire" />
          </ProtectedRoute>
        }
      >
        <Route index element={<LocataireDashboard />} />
        <Route path="bookings" element={<LocataireBookings />} />
        <Route path="bookings/:id" element={<BookingDetailPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Routes Gérant */}
      <Route
        path="/gerant"
        element={
          <ProtectedRoute allowedRoles={['ROLE_GERANT']}>
            <DashboardLayout userType="gerant" />
          </ProtectedRoute>
        }
      >
        <Route index element={<GerantDashboard />} />
        <Route path="apartments" element={<GerantApartments />} />
        <Route path="apartments/new" element={<GerantApartmentForm />} />
        <Route path="apartments/:id/edit" element={<GerantApartmentForm />} />
        <Route path="calendar" element={<GerantCalendar />} />
        <Route path="bookings" element={<GerantBookings />} />
        <Route path="bookings/:id" element={<BookingDetailPage />} />
        <Route path="bookings/invitation/new" element={<CreateInvitation />} />
        <Route path="invoices" element={<GerantInvoices />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Routes Admin (Super User) */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['ROLE_ADMIN']}>
            <DashboardLayout userType="admin" />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="gerants" element={<AdminGerants />} />
        <Route path="locataires" element={<AdminLocataires />} />
        <Route path="apartments" element={<AdminApartments />} />
        <Route path="apartments/:id/edit" element={<GerantApartmentForm />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="bookings/:id" element={<BookingDetailPage />} />
        <Route path="bookings/invitation/new" element={<CreateInvitation />} />
        <Route path="invoices" element={<GerantInvoices />} />
        <Route path="calendar" element={<GerantCalendar />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
